import { motion, useAnimation, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";

/* ================= Q2 STATS ================= */
const stats = [
  { value: 15, suffix: "+", label: "Industries Served", light: true },
  { value: 100, suffix: "%", label: "Design Approval", light: false },
  { value: 100, suffix: "+", label: "Custom Designs", light: true },
  { value: 5000, suffix: "+", label: "Apparels Sold", light: false },
  { value: 6, suffix: "+", label: "Cities Served", light: true },
  { value: 20, suffix: "+", label: "Bulk Orders Done", light: false },
  { value: 90, suffix: "%", label: "Repeat Clients", light: true },
];

export default function Hero() {
  return (
    <>
      <section className="relative w-screen bg-black text-white overflow-hidden">
        <div className="
          grid grid-cols-1 md:grid-cols-[52%_48%] grid-rows-2
          min-h-[65vh]
          sm:min-h-[90vh]
          md:min-h-[1050px]
          lg:min-h-[1150px]
        ">

          {/* ================= Q1 ================= */}
          <div className="border-b md:border-r border-white/30 border-[1.5px] px-[6vw] py-[8vh] flex flex-col justify-center">
            <h1
              style={{
                fontFamily: "'PP Neue Montreal', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(36px, 4.5vw, 60px)",
                lineHeight: "1.18",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "#f2f2f2",
              }}
            >
              <span className="block">WHERE BRAND</span>
              <span className="block">IDENTITY MEETS</span>
              <span className="block">WEARABLE DESIGN</span>
            </h1>

            <p className="mt-6 text-[#9a9a9a] text-[14px] md:text-[15px] leading-[1.7] max-w-[420px]">
              We help teams, brands, and creators translate their identity
              into breathable, wearable merchandise — guided from concept
              to production.
            </p>
          </div>

          {/* ================= IMAGE ================= */}
          <div className="row-span-2 relative">
            <img
              src="/Website-assests/hero.jpeg"
              alt="Hero Hoodie"
              className="absolute inset-0 w-full h-full object-contain lg:object-cover"
            />

            {/* Tablet + Mobile centered, Desktop right aligned */}
            <div className="absolute 
                bottom-8 md:bottom-12
                left-1/2 -translate-x-1/2
                lg:left-auto lg:translate-x-0 lg:right-12
                z-20">
              <AnimatedThumbnails />
            </div>
          </div>

          {/* ================= Q2 ================= */}
          <div
            className="
              border-white/30 border-[1.5px] md:border-r
              relative overflow-hidden px-[6vw]
              min-h-[240px] md:min-h-[unset]
            "
            style={{
              WebkitMaskImage:
                "linear-gradient(90deg, transparent 0%, black 6%, black 94%, transparent 100%)",
              maskImage:
                "linear-gradient(90deg, transparent 0%, black 6%, black 94%, transparent 100%)",
            }}
          >

            {/* MOBILE FLOW */}
            <motion.div
              className="md:hidden absolute top-6 flex gap-4"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
              style={{ width: "max-content" }}
            >
              {[...stats, ...stats].map((item, i) => (
                <StatCard key={`m-${i}`} {...item} />
              ))}
            </motion.div>

            {/* DESKTOP FLOW (unchanged) */}
            <motion.div
              className="hidden md:flex absolute top-[6vh] gap-6"
              animate={{ x: [0, -1200] }}
              transition={{ repeat: Infinity, duration: 22, ease: "linear" }}
              style={{ width: "fit-content" }}
            >
              {[...stats, ...stats].map((item, i) => {
                const isTopRow = i % 2 === 0;
                return (
                  <div key={`d-${i}`} className="flex flex-col gap-6" style={{ width: "220px" }}>
                    {isTopRow ? <StatCard {...item} /> : <div className="h-[210px]" />}
                    {!isTopRow ? <StatCard {...item} /> : <div className="h-[210px]" />}
                  </div>
                );
              })}
            </motion.div>

          </div>
        </div>
      </section>
    </>
  );
}


/* ================= STAT CARD ================= */

function StatCard({ value, suffix, label, light }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let current = 0;
    const step = Math.max(1, Math.floor(value / 18));
    const interval = setInterval(() => {
      current += step;
      if (current >= value) {
        current = value;
        clearInterval(interval);
      }
      setCount(current);
    }, 20);
    return () => clearInterval(interval);
  }, [value]);

  return (
    <div
      className={`
        group relative
        w-[150px] h-[140px]
        md:w-[220px] md:h-[210px]
        p-6 md:p-9
        flex flex-col justify-between
        transition-transform duration-300 ease-out
        hover:scale-[1.08]
        ${light ? "bg-[#f1f1f1] text-black" : "bg-[#1f1f1f] text-white"}
      `}
    >
      <div className="text-[30px] md:text-[46px] font-medium tracking-wide">
        {count}{suffix}
      </div>
      <div className={`text-[14px] md:text-[20px] tracking-wider ${light ? "text-gray-700" : "text-gray-300"}`}>
        {label}
      </div>
    </div>
  );
}


/* ================= THUMBNAILS ================= */

function AnimatedThumbnails() {
  const ref = useRef(null);
  const controls = useAnimation();

  const isInView = useInView(ref, {
    once: true,
    margin: "-100px",
  });

  useEffect(() => {
    if (isInView) controls.start("visible");
  }, [isInView, controls]);

  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  const item = {
    hidden: { x: "100vw", opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <div ref={ref}>
      <motion.div
        className="grid grid-cols-3 gap-20 md:gap-24 lg:gap-10"
        variants={container}
        initial="hidden"
        animate={controls}
      >
        {["hero img1.png", "hero img2.png", "hero img3.png"].map((img, i) => (
          <motion.div
            key={i}
            variants={item}
            className="
              border-2 border-white/60
              w-[100px] h-[120px]
              md:w-[140px] md:h-[170px]
              lg:w-[190px] lg:h-[210px]
              bg-black"
          >
            <img
              src={`/Website-assests/${img}`}
              alt={`thumb ${i}`}
              className="w-full h-full object-cover"
            />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
